import React from 'react';
import { TestimonialCard } from './TestimonialCard';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRightIcon, ClipboardListIcon, MicroscopeIcon, FileTextIcon, MessageCircleIcon, CreditCardIcon } from 'lucide-react';
export function SolutionSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const steps = [{
    icon: <ClipboardListIcon className="w-8 h-8 text-teal-600" data-id="element-451" data-mp="139s70orffrf" />,
    title: 'STEP 1: Pre-Session Biological Dossier Review',
    desc: "Before you ever get on the call you'll submit the most important details. Labs, imaging, medications, previous diagnoses, symptoms, everything. Either Brenden or Dr. Ali will review your case. Looking for patterns. Spotting what's been missed."
  }, {
    icon: <MicroscopeIcon className="w-8 h-8 text-teal-600" data-id="element-452" data-mp="ktmy0ke07z4e" />,
    title: 'STEP 2: 2–3 Hour Precision Cell Command Session',
    desc: "You'll spend 2–3 HOURS with either Brenden or Dr. Ali... Going through your case line by line. They'll dive into what's REALLY going on. Not just surface symptoms... But the root biological causes driving everything. They'll be getting a deep understanding of what's happening with you at the CELLULAR level."
  }, {
    icon: <FileTextIcon className="w-8 h-8 text-teal-600" data-id="element-453" data-mp="3kadl7w25yum6" />,
    title: 'STEP 3: Your Precision Cell Blueprint',
    desc: "After your session (and sometimes even right at the end of your session) you'll get a written, holistic program EXPERTLY crafted by Dr. Ali or Brenden. It's the exact roadmap showing you: What to do, When to do it, In what order, What tests to further run, Which compounds to use, Dosing and timing guidelines etc."
  }, {
    icon: <MessageCircleIcon className="w-8 h-8 text-teal-600" data-id="element-454" data-mp="6ufozter0sfy" />,
    title: 'STEP 4: 30 Day Implementation Support',
    desc: "You're not being abandoned after the session. For 30 days after your call you'll be able to receive basic support from your specialist, so if questions come up about your program, or you need help fine-tuning the protocol... You're covered."
  }, {
    icon: <CreditCardIcon className="w-8 h-8 text-teal-600" data-id="element-455" data-mp="27y8ay4paq7oy" />,
    title: 'STEP 5: Full Transformation Program Credit',
    desc: 'If you decide you need longer-term, hands-on support, you can upgrade to a full Precision Cell™ Transformation Program within 30 days. Your entire $5,000 Advanced lite program investment is applied as credit, so you do not lose anything or pay twice for the same transformation work.'
  }];
  return <div ref={ref} className="grain-section relative w-full bg-white px-6 py-20 md:py-28" data-id="element-456" data-mp="2rkxo0rgx68o9">
      {/* Chapter indicator */}
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={inView ? {
      opacity: 1,
      x: 0
    } : {}} transition={{
      duration: 0.8
    }} className="max-w-5xl mx-auto mb-14 md:mb-16" data-id="element-457" data-mp="35kp899y14lap">
        <div className="flex flex-col md:flex-row md:items-center gap-8 mb-8" data-id="element-458" data-mp="1b9ahqqx72htw">
          <div className="text-8xl md:text-9xl font-black text-teal-50 font-sans tracking-tighter" data-id="element-459" data-mp="16a7cb5day9le">
            02
          </div>
          <div className="h-px w-24 bg-teal-100 hidden md:block" data-id="element-460" data-mp="3mut4a8np1llq"></div>
          <div data-id="element-461" data-mp="3mu17iuid1htv">
            <p className="text-xs text-teal-600 font-bold tracking-widest uppercase mb-3" data-id="element-462" data-mp="3mwnf2u593xc3">
              Chapter Two
            </p>
            <h2 className="text-4xl md:text-6xl font-bold text-ink tracking-tight" data-id="element-463" data-mp="3d4hsjzxrq84a">
              Why Most "Expert Consults" <br data-id="element-464" data-mp="2z46x0jlik5cs" />
              <span className="text-teal-600" data-id="element-465" data-mp="2z3nfv53otkzt">Are Basically Useless</span>
            </h2>
          </div>
        </div>
      </motion.div>

      <div className="max-w-5xl mx-auto space-y-14 md:space-y-16" data-id="element-466" data-mp="2p729afc0s1tv">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="space-y-8 text-xl text-ink-light leading-relaxed font-light" data-id="element-467" data-mp="2p8vhhz2a6u3z">
          <p data-id="element-468" data-mp="sdh95kvspajf">
            Let's be honest about what usually happens when you book a
            consultation. You pay your money... You show up to the call... And
            the "expert" asks you a bunch of surface-level questions they
            could've asked beforehand.
          </p>
          <p data-id="element-469" data-mp="2fg5y9udwepq6">
            They nod along while you explain your situation... (Which they're
            hearing for the FIRST time...) Then they give you some generic
            advice... Maybe recommend a few supplements... And send you on your
            way.
          </p>

          <div className="bg-page-bg p-10 rounded-3xl border border-black/5 text-center" data-id="element-470" data-mp="20rt0crv92wt1">
            <p className="text-2xl font-bold text-ink" data-id="element-471" data-mp="1wqhhd56vt9tt">
              That's not mastery. That's just expensive guessing. And you don't
              need more guesses. You need ANSWERS.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="space-y-8 pt-8 md:space-y-10 md:pt-10" data-id="element-472" data-mp="1mr8hjvpc50zj">
          <h3 className="text-3xl md:text-5xl font-bold text-center text-ink tracking-tight" data-id="element-473" data-mp="1cocxev9wp8v6">
            How Meeting With the Master: <br data-id="element-474" data-mp="1hnfyy5qogwye" />
            <span className="text-teal-600" data-id="element-475" data-mp="1hp0u9xktgjxb">ADVANCED Actually Works</span>
          </h3>

          <div className="grid gap-8" data-id="element-476" data-mp="1mlr96hkvx9h5">
            {steps.map((step, index) => <div key={index} className="bg-white border border-black/5 rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-300" data-id="element-477" data-mp="ozahbrux7o2s" data-mpi={index}>
                <div className="flex flex-col md:flex-row gap-6 md:gap-8" data-id="element-478" data-mp="tvsgmc7heu7q">
                  <div className="bg-teal-50 p-4 rounded-2xl self-start" data-id="element-479" data-mp="yvx08b3os526">
                    {step.icon}
                  </div>
                  <div className="space-y-4" data-id="element-480" data-mp="68ke221ftz9e">
                    <h4 className="text-xl md:text-2xl font-bold text-ink" data-id="element-481" data-mp="67rf3n8q0e1t">
                      {step.title}
                    </h4>
                    <p className="text-lg text-ink-light font-light leading-relaxed" data-id="element-482" data-mp="b5j4oalw7o2u">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.65,
        duration: 0.7
      }} className="flex justify-center" data-id="element-483" data-mp="cx0rxd480rjt">
          <a href="#advanced-price" className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-9 py-5 text-center text-base font-black text-white shadow-[0_12px_32px_rgba(5,150,105,0.3)] ring-1 ring-emerald-500 transition-all duration-200 hover:-translate-y-1 hover:bg-emerald-700 hover:shadow-[0_18px_42px_rgba(5,150,105,0.35)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300/50 sm:w-auto sm:text-lg" data-id="element-484" data-mp="3gdq1l7z3ohdp">
            Book Your Meeting With the Master: Advanced
            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-485" data-mp="185ulfskm9tjq" />
          </a>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} animate={inView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        delay: 0.7,
        duration: 0.8
      }} data-id="element-486" data-mp="1d4xtllvw8fvx">
          <TestimonialCard text="Thank you Ali... You and Brenden have been a light for both of us. You have done what our entire team of medical professionals were not able to do. I am looking forward to doing a program with you myself after my husband's recovery." highlight={true} profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/rdsSxPNyzo6kTft2Q6Yhgt/Screenshot_2025-11-10_200825.png" name="Badar A., Peptide Science Institute Transformation Services Client" data-id="element-487" data-mp="136871ft1y79c" />
        </motion.div>
      </div>
    </div>;
}